import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  GraduationCap, ArrowRight, CircleNotch, Leaf, BookOpen,
} from "@phosphor-icons/react";
import { schoolRedeem } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/**
 * Public entry page for school classes. No auth required — students paste
 * the code their teacher gave them + a nickname → receive a read-only session.
 *
 * Layout inspired by Kahoot.it: big, bold, one focus.
 */
export default function SchoolEnter() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [code, setCode] = useState("");
  const [nickname, setNickname] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!code.trim() || !nickname.trim()) {
      setError("Bitte Code und Vornamen ausfüllen.");
      return;
    }
    setLoading(true);
    try {
      await schoolRedeem(code.trim(), nickname.trim());
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Hat nicht geklappt. Prüf den Code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      data-testid="school-enter-page"
      style={{
        background:
          "radial-gradient(circle at 20% 10%, #0E1A14 0%, #0A0A0A 50%), radial-gradient(circle at 80% 90%, #1A160A 0%, #141414 60%)",
      }}
    >
      <Helmet>
        <title>Schulmodus · bodenblick.com</title>
      </Helmet>

      <div className="w-full max-w-md">
        <Link to="/insights" className="text-xs text-zinc-400 flex items-center gap-1 hover:text-white">
          <Leaf size={12} /> bodenblick.com
        </Link>

        <div className="eid-card p-8 mt-4">
          <div className="flex items-center gap-3">
            <span className="p-2 rounded-sm bg-[#00FA9A]/10 text-[#00FA9A]">
              <GraduationCap size={22} weight="fill" />
            </span>
            <div>
              <div className="eid-label">Schulmodus</div>
              <h1 className="text-2xl font-medium tracking-tight">Code eingeben</h1>
            </div>
          </div>

          <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
            Gib den <strong>6-stelligen Code</strong> ein, den du von deiner
            Lehrerin oder deinem Lehrer bekommen hast. Dann kannst du
            eine Stunde lang echte Satelliten-, Wetter- und Klimadaten
            für Österreich und Deutschland erkunden.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="eid-label">Code</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="z. B. UTXE3V"
                maxLength={20}
                data-testid="school-code-input"
                autoFocus
                autoCapitalize="characters"
                autoComplete="off"
                className="mt-1 w-full text-center text-3xl tracking-[0.4em] font-mono bg-[#0A0A0A] border-2 border-[#00FA9A]/40 rounded-sm px-4 py-3 focus:outline-none focus:bg-[#121212]"
              />
            </div>

            <div>
              <label className="eid-label">Dein Vorname</label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Lena"
                maxLength={40}
                data-testid="school-nickname-input"
                className="mt-1 w-full border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#00FA9A]/40"
              />
            </div>

            {error && (
              <div
                className="text-sm text-[#FF7A45] bg-[#FF5500]/10 border border-[#E8C2B4] rounded-sm px-3 py-2"
                data-testid="school-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="school-submit"
              className="w-full flex items-center justify-center gap-2 bg-[#00FA9A] text-black hover:bg-[#33FBAE] rounded-sm px-4 py-3 text-base font-medium disabled:opacity-60"
            >
              {loading ? (
                <><CircleNotch size={16} className="animate-spin" /> Verbinde …</>
              ) : (
                <>Los geht's <ArrowRight size={16} weight="bold" /></>
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-white/10 text-xs text-zinc-400 flex items-start gap-2">
            <BookOpen size={14} className="text-[#00FA9A] shrink-0 mt-0.5" />
            <span>
              Schulmodus ist <strong>kostenlos</strong> & anonym. Wir speichern
              keine persönlichen Daten. Dein Zugang endet, wenn der Code abläuft.
              Lehrer:innen bekommen Codes auf Anfrage an {" "}
              <a href="mailto:schulen@bodenblick.com" className="underline">schulen@bodenblick.com</a>.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
